/**
 * Lowers the export scene's SceneSolids to prisms and their planar boundary
 * faces: closed footprints extrude directly, and open paths become per-edge
 * boxes, since a solid export format has no zero-thickness concept.
 */

import { type Point, ringSignedArea } from '@kaiizen/kai-floor-plan';
import { type SceneSolid, type SceneSolidFolder } from './mesh3d';
import { face, FACE_EPSILON, mirror, type PlanarFace } from './planarFace';

/** Exported thickness of glass panes and other true planes (feet). */
const GLASS_SLAB_THICKNESS = 1 / 12;

/** The plan footprint of one path edge thickened perpendicular by `thickness`. */
const edgeRing = (from: Point, to: Point, thickness: number): Point[] => {
  const deltaX = to.x - from.x, deltaY = to.y - from.y;
  const edgeLength = Math.hypot(deltaX, deltaY) || 1;
  const offsetX = (deltaY / edgeLength) * (thickness / 2), offsetY = (-deltaX / edgeLength) * (thickness / 2);
  return [
    { x: from.x + offsetX, y: from.y + offsetY },
    { x: to.x + offsetX, y: to.y + offsetY },
    { x: to.x - offsetX, y: to.y - offsetY },
    { x: from.x - offsetX, y: from.y - offsetY },
  ];
};

export interface ScenePrism {
  name: string;
  /** The edge segment's folder when this prism is part of a door or window segment. */
  folder?: SceneSolidFolder;
  /** Plan-space footprint rings, holes after the first. */
  rings: Point[][];
  base: number;
  top: number;
}

/**
 * Every non-wall solid as a prism: closed footprints extrude directly; open
 * paths become per-edge boxes: door panels at their real width, true planes
 * (glass, niche reveals) at the 1-inch slab.
 */
export const scenePrisms = (solids: SceneSolid[]): ScenePrism[] => {
  const prisms: ScenePrism[] = [];
  for (const { layer, name, folder, rings, closed, width, base, top } of solids) {
    if (top - base < FACE_EPSILON) continue;
    const prismName = name ?? layer;
    if (closed) {
      prisms.push({ name: prismName, folder, rings, base, top });
      continue;
    }
    const thickness = width || GLASS_SLAB_THICKNESS;
    const path = rings[0];
    for (let i = 0; i + 1 < path.length; i++) {
      if (Math.hypot(path[i + 1].x - path[i].x, path[i + 1].y - path[i].y) < FACE_EPSILON) continue;
      prisms.push({ name: prismName, folder, rings: [edgeRing(path[i], path[i + 1], thickness)], base, top });
    }
  }
  return prisms;
};

/**
 * A capped prism's boundary: top and bottom caps and one side face per ring
 * edge. Material lies left of travel (outer ring counterclockwise, holes
 * clockwise), so every side face's outward normal is right of travel; the
 * outward hint is the orientation decision, so it must be material-correct.
 */
export const prismFaces = ({ rings, base, top }: ScenePrism): PlanarFace[] => {
  const mirrored = rings.map(ring => ring.map(mirror)).map((ring, ringIndex) => {
    const counterclockwise = ringSignedArea(ring) >= 0;
    return (ringIndex === 0) === counterclockwise ? ring : [...ring].reverse();
  });
  const faces: PlanarFace[] = [
    face(mirrored, point => ({ x: point.x, y: point.y, h: top }), { x: 0, y: 0, h: 1 }),
    face(mirrored, point => ({ x: point.x, y: point.y, h: base }), { x: 0, y: 0, h: -1 }),
  ];
  for (const ring of mirrored) {
    for (let i = 0; i < ring.length; i++) {
      const from = ring[i], to = ring[(i + 1) % ring.length];
      const length = Math.hypot(to.x - from.x, to.y - from.y);
      if (length < FACE_EPSILON) continue;
      faces.push(face(
        [[{ x: 0, y: base }, { x: length, y: base }, { x: length, y: top }, { x: 0, y: top }]],
        facePoint => {
          const fraction = facePoint.x / length;
          return { x: from.x + (to.x - from.x) * fraction, y: from.y + (to.y - from.y) * fraction, h: facePoint.y };
        },
        { x: to.y - from.y, y: from.x - to.x, h: 0 },
      ));
    }
  }
  return faces;
};
